import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Map, Sparkles, User, Sun, Moon, TrendingUp, Building2, Crown, LogIn, Shield, Store, Globe, HeartPulse, Car, Home as HomeIcon, Search, Newspaper } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import InstallBanner from "./InstallBanner";
import NotificationsBell from "./NotificationsBell";
import AppInfoStrip from "./AppInfoStrip";
import SubscriptionNotice from "./SubscriptionNotice";

const navItems = [
  { to: "/feed", label: "Início", icon: Home, testid: "nav-home" },
  { to: "/empresas", label: "Explorar", icon: Building2, testid: "nav-explore" },
  { to: "/ia", label: "IA", icon: Sparkles, testid: "nav-ai", center: true },
  { to: "/mapa", label: "Mapa", icon: Map, testid: "nav-map" },
  { to: "/perfil", label: "Perfil", icon: User, testid: "nav-profile" },
];

const sideItems = [
  { to: "/feed", label: "Início", icon: Home, testid: "side-home" },
  { to: "/busca", label: "Buscar", icon: Search, testid: "side-search" },
  { to: "/noticias", label: "Central de notícias", icon: Newspaper, testid: "side-news-center" },
  { to: "/ia", label: "Pergunte à IA", icon: Sparkles, testid: "side-ai" },
  { to: "/mapa", label: "Mapa", icon: Map, testid: "side-map" },
  { to: "/empresas", label: "Empresas", icon: Building2, testid: "side-business" },
  { to: "/imoveis", label: "Guia Imóveis", icon: HomeIcon, testid: "side-imoveis" },
  { to: "/medicos", label: "Guia Médicos", icon: HeartPulse, testid: "side-medicos" },
  { to: "/veiculos", label: "Guia Auto", icon: Car, testid: "side-veiculos" },
  { to: "/minha-empresa", label: "Minha empresa", icon: Store, testid: "side-mybusiness" },
  { to: "/insights", label: "Insights IA", icon: TrendingUp, testid: "side-insights" },
  { to: "/planos", label: "Planos", icon: Crown, testid: "side-plans" },
  { to: "/perfil", label: "Perfil", icon: User, testid: "side-profile" },
];

function Logo({ className = "h-10" }) {
  return (
    <div className="flex items-center">
      <img src="/logo-h.webp" alt="Guia de Jampa AI" width="188" height="98" className={`${className} w-auto object-contain`} />
    </div>
  );
}

export default function Layout() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed left-0 top-0 h-screen w-64 border-r border-border bg-card/40 px-4 py-6 z-50 overflow-y-auto">
        <div className="px-2 mb-6 shrink-0"><Logo className="h-[73px]" /></div>
        <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
          {sideItems.filter((it) => user?.role !== "ai_operator" || !["/planos", "/minha-empresa"].includes(it.to)).map((it) => (
            <NavLink
              key={it.testid}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <it.icon className="w-5 h-5" /> {it.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="side-admin"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <Shield className="w-5 h-5" /> Admin
            </NavLink>
          )}
          {user?.role === "ai_operator" && (
            <NavLink to="/operador-ia" data-testid="side-ai-operator"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <Sparkles className="w-5 h-5" /> Operador IA
            </NavLink>
          )}
          <NavLink to="/" data-testid="side-site"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <Globe className="w-5 h-5" /> Site institucional
          </NavLink>
        </nav>
        <div className="flex items-center gap-2 mt-4 shrink-0">
          {user && <NotificationsBell />}
          <button data-testid="theme-toggle-desktop" onClick={toggle} className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted transition-colors flex-1">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            {theme === "dark" ? "Modo claro" : "Modo escuro"}
          </button>
        </div>
        {!user && (
          <button data-testid="side-login" onClick={() => navigate("/login")} className="mt-2 shrink-0 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground">
            <LogIn className="w-4 h-4" /> Entrar
          </button>
        )}
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-40 glass-header px-4 py-2.5 safe-top flex items-center justify-between gap-2">
        <Logo className="h-[73px]" />
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid="mobile-search" onClick={() => navigate("/busca")} aria-label="Buscar" className="p-2 rounded-full hover:bg-muted transition-colors">
            <Search className="w-5 h-5" />
          </button>
          {user && <NotificationsBell />}
          <button data-testid="theme-toggle-mobile" onClick={toggle} aria-label="Alternar tema" className="p-2 rounded-full hover:bg-muted transition-colors">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          {!user && (
            <button data-testid="mobile-login" onClick={() => navigate("/login")} aria-label="Entrar" className="p-2 rounded-full hover:bg-muted transition-colors">
              <LogIn className="w-5 h-5" />
            </button>
          )}
        </div>
      </header>

      {/* LED display ticker — full-width, below header on mobile and top on desktop.
          Mobile total header height = 68px (header) + strip below (~84px) = ~152px. */}
      <div className="md:pl-64 sticky top-[68px] md:top-0 z-40 px-3 md:px-6 pt-2 md:pt-4 pb-1 md:pb-2 bg-background/95 backdrop-blur-md">
        <AppInfoStrip />
      </div>

      {/* Main content */}
      <main className="md:pl-64">
        <div className="max-w-3xl mx-auto px-4 md:px-8 pb-28 md:pb-12 pt-4 md:pt-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <SubscriptionNotice />
            <Outlet />
          </motion.div>
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-nav safe-bottom px-2 pt-2 pb-2">
        <div className="flex items-center justify-around">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            if (it.center) {
              return (
                <NavLink key={it.to} to={it.to} data-testid={it.testid} aria-label={it.label}
                  className="flex flex-col items-center -mt-6">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-primary/30 transition-transform active:scale-90 ${active ? "bg-primary" : "bg-primary"}`}>
                    <it.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <span className="text-[10px] mt-1 font-medium text-primary">{it.label}</span>
                </NavLink>
              );
            }
            return (
              <NavLink key={it.to} to={it.to} data-testid={it.testid} aria-label={it.label}
                className="flex flex-col items-center gap-0.5 px-3 py-1.5">
                <it.icon className={`w-5 h-5 transition-colors ${active ? "text-primary" : "text-muted-foreground"}`} />
                <span className={`text-[10px] font-medium transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <InstallBanner />
    </div>
  );
}
